import React, { useEffect, useState } from "react";
import { applySortFilter, isEmptyFilter } from "../../utils/tableFunctions";
import {
  Box,
  Card,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import MoreMenu from "./MoreMenu";
import {
  downloadActiveMerchantUserEmails,
  getMerchantUserFilterConditions,
  Log,
  sendDataWithPost,
} from "../../services/services";
import EditDialogs from "./Dialogs/EditDialogs";
import DeleteDialog from "./Dialogs/DeleteDialog";
import RestoreDialog from "./Dialogs/RestoreDialog";
import { StyledTableRow } from "../TableRow";
import FormSearch from "../FormSearch";
import CircularLoader from "../circularLoader";
import SearchPrompt from "../SearchPrompt";
import { pageNumber, rowsPerPage } from "../../utils/constants";
import Pagination from "../Pagination";
import Label from "../Label";
import moment from "moment";
import AccountDetails from "../../pages/details/admin/MerchantUserDetails";
import CommonDialog from "../CustomDialog";
import HeaderHelpPage from "../HeaderHelpPage";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "email", label: "Email" },
  { id: "business_entities", label: "Business Entities" },
  { id: "merchant_group", label: "Merchant Group" },
  { id: "last_login", label: "Last Login" },
  { id: "is_active", label: "Active" },
  { id: "" },
];

const ViewMerchantUser = ({ userRole }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState(defaultValue);
  const [inputValues, setInputValues] = useState({});

  // dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [itemId, setItemId] = useState(null);
  // the list is only queried once the user has searched, so the tab opens fast
  const [searched, setSearched] = useState(false);

  //filter
  const [accountFilter, setAccountFilter] = useState({});
  const [fLoaded, setFLoaded] = useState(false);

  const CustomTableCell = ({ align, field, id, sx }) => {
    return (
      <TableCell
        align={align}
        onClick={() => {
          // window.open(`/3/app/merchant-user-details/${id}/`, "_blank");
          setItemId(id);
        }}
        sx={{ ...sx }}
      >
        {field}
      </TableCell>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryPage = (queryParams, params) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/merchant/merchant_user/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, renderData, setLoaded, () => {
      setPagination(queryParams);
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    })
  };

  const filteredList = applySortFilter(
    tableData.results || [],
    order,
    orderBy
  );

  const isItemNotFound = tableData.results.length === 0;

  const renderData = res => {
    const newData = res.results.map(item => ({
      ...item,
      // business_entity: item.business_entity?.business_name,
      merchant_group: item.merchant_group?.name,
      business_entities: item.merchant_user_business_entity
      ?.map((item) => item.business_name)
      .join(", ")
    }))
    
    setTableData({
      ...res,
      results: newData
    });
  }

  useEffect(() => {
    getMerchantUserFilterConditions(setAccountFilter, setFLoaded);
  }, []);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenRestoreDialog(false);
  };

  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the tab used to
    // load with on first render
    const fParams = isEmptyFilter(params) ? { ...defaultValue } : params;
    const newPrams = {
      ...fParams,
      business_entity: fParams?.business_entity?.id,
      email: inputValues?.email?.trim() || fParams.email?.trim(),
      business_entity_input: inputValues?.business_entity?.trim() || null
    }
    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newPrams.is_active === 'all') delete newPrams.is_active;
    Log.info('Click button [Apply Filter] - Search Merchant User list', newPrams);

    setSearched(true);
    setFilter(newPrams);
    queryPage(newPagination, newPrams);
  };

  const schema = [
    {
      type: "search-select",
      label: "Email",
      param: "email",
      options: accountFilter?.email?.map((email) => email),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Business Entity",
      param: "business_entity",
      options: accountFilter?.business_entity?.map((entity) => ({
        label: entity.name,
        id: entity.id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "select",
      label: "Group",
      param: "group",
      options: accountFilter?.group?.map((grp) => ({
        id: grp.id,
        name: grp.name,
      }))
    },
    {
      type: "select",
      label: "Active",
      param: "is_active",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        onDownload={downloadActiveMerchantUserEmails}
        defaultValue={defaultValue}
      />
      {!searched ?
        <SearchPrompt />
        : loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {loaded &&
                    filteredList.map((row) => {
                        const {
                          id,
                          email,
                          is_active,
                          merchant_group,
                          business_entities,
                          last_login
                        } = row;

                        const namesArray = business_entities?.split(", ") || [];
                        const displayNames = namesArray.length > 3 ? [...namesArray.slice(0, 3), "..."] : namesArray;

                        return (
                          <StyledTableRow
                            hover
                            key={id}
                            sx={{ cursor: "pointer" }}
                          >
                            <CustomTableCell
                              field={email}
                              id={id}
                              sx={{ verticalAlign: "top"}}
                            />
                            <CustomTableCell
                              field={
                                <Tooltip
                                  title={
                                    namesArray.length > 3 ? (
                                      <Box component="ul" sx={{ listStyle: "none", p: 1, m: 0 }}>
                                        {namesArray.map((name) => (
                                          <li key={name}>
                                            <Typography variant="body2">{name}</Typography>
                                          </li>
                                        ))}
                                      </Box>
                                    ) : ""
                                  }
                                  disableHoverListener={namesArray.length <= 3}
                                  arrow
                                >
                                  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                                    {displayNames.map((name) => (
                                      <li key={name}>{name}</li>
                                    ))}
                                  </Box>
                                </Tooltip>
                              }
                              id={id}
                            />
                            <CustomTableCell
                              field={merchant_group}
                              id={id}
                              sx={{ verticalAlign: "top"}}
                            />
                            <CustomTableCell
                              field={last_login && moment(last_login).format("DD/MM/YYYY")}
                              id={id}
                              sx={{ verticalAlign: "top"}}
                            />
                            <CustomTableCell
                              field={
                                <Label color={is_active ? "success" : "error"}>
                                  {is_active ? "YES" : "NO"}
                                </Label>
                              }
                              id={id}
                              sx={{ verticalAlign: "top"}}
                            />

                            <TableCell align="right" sx={{ verticalAlign: "top"}}>
                              {userRole && (userRole.includes("Edit") ||
                                userRole.includes("Delete")) && (
                                <MoreMenu
                                  setOpenDeleteDialog={setOpenDeleteDialog}
                                  setOpenEditDialog={setOpenEditDialog}
                                  setOpenRestoreDialog={setOpenRestoreDialog}
                                  setRow={() => setSelectedId(id)}
                                  userRole={userRole}
                                  is_active={is_active}
                                />
                              )}
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
      </Card>
      :
      <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditDialogs
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
          {openRestoreDialog && (
            <RestoreDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
        </>
      )}

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Merchant User Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <AccountDetails itemId={itemId} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default ViewMerchantUser;
